import React from 'react';
import styled from 'styled-components';

/**
 * Total loop duration. Every block shares this one `assemble` timeline; only `animation-delay`
 * differs per block. Kept in one place so the phase-percentage table in the keyframes below and
 * the millisecond delay math in BLOCKS stay honest against each other.
 */
const LOOP_MS = 4500;

/** Group beat (ms between region groups) and micro-stagger (ms between blocks in the same
 * group) -- see PLAN.md's "Rhythm via grouped stagger". Kept low enough that the `lines` group
 * (which has the largest cumulative stagger of any group -- not because it has the most
 * members, `cards` has more, but because `cards`'s per-member stagger is compressed by
 * INTRA_CARD_STAGGER_MS below instead of the full MICRO_STAGGER_MS) doesn't eat the whole
 * hold phase -- see budget rule below.
 *
 * Budget rule: the moment the whole page reads as "assembled" is [maxDelay + settleEndMs,
 * minDelay + dissolveStartMs], i.e. assembledWindowMs = holdPhaseMs - maxStaggerSpreadMs (spread
 * = last block's delayForBlock() output, assuming the first block's delay is 0). This window must
 * stay >= ~700ms -- it's the payoff of the whole animation, not just a frame in passing.
 *
 * Current numbers: hold phase = 40%-72% of LOOP_MS (4500ms) = 1440ms. Max spread = the `lines`
 * group's last block = GROUP_ORDER.lines * GROUP_BEAT_MS + 3 * MICRO_STAGGER_MS
 * = 3*140 + 3*40 = 540ms. Assembled window = 1440 - 540 = 900ms. If a future edit adds blocks,
 * groups, or changes LOOP_MS/hold percentages, recompute this before shipping. */
const GROUP_BEAT_MS = 140;
const MICRO_STAGGER_MS = 40;
/** Intra-card offset for the 2 text lines relative to their card's own thumbnail -- deliberately
 * much smaller than MICRO_STAGGER_MS so a whole card (thumb + 2 lines) reads as one arriving
 * unit instead of 3 separately-timed blocks (see delayForBlock). */
const INTRA_CARD_STAGGER_MS = 15;

/** Skeleton palette lifted from the app's existing skeletons (menu-table/skeleton.tsx,
 * product-grid.tsx) so this loader doesn't invent a new one. */
const TONE_BASE = '#e8ecee';
const TONE_HIGHLIGHT = '#f5f8fa';
const TONE_HAIRLINE = '1px solid rgba(0, 0, 0, 0.04)';
/** Dutchie green at low alpha, ties the stage back to the rail without competing with it. */
const TONE_ACCENT = 'rgba(0, 61, 41, 0.14)';

type BlockTone = 'accent' | 'base';

type BlockDescriptor = {
  id: string;
  /** Region group -- drives the group-beat portion of the delay, and documents intent. */
  group: 'cards' | 'header' | 'hero' | 'lines';
  /** Index within its group -- drives the micro-stagger portion of the delay. */
  indexInGroup: number;
  /** Position/size within the ~420x300 stage, in px. */
  x: number;
  y: number;
  w: number;
  h: number;
  radius: number;
  tone: BlockTone;
  /** Scatter vector it flies in from (px, relative to final position). */
  dx: number;
  dy: number;
  /** Scatter vector it dissolves out along -- deliberately not `-dx/-dy`, so arrival and
   * departure never look like the same motion played backwards. */
  ddx: number;
  ddy: number;
  rot: number;
  /** Overshoot scale at the fly-in peak. Big surfaces get a smaller overshoot (see PLAN.md:
   * "back-easing on big surfaces looks sloppy") and use the quint-out timing function below. */
  overshoot: number;
  big?: boolean;
  /** Whether the shimmer sweep runs across this block during the hold phase. */
  shimmer?: boolean;
};

const GROUP_ORDER: Record<BlockDescriptor['group'], number> = {
  header: 0,
  hero: 1,
  cards: 2,
  lines: 3,
};

/** Static layout: a miniature webpage (header / hero / card row / paragraph lines) inside a
 * 420x300 stage. Positions are hand-placed and deterministic -- no measurement, no state. */
const BLOCKS: BlockDescriptor[] = [
  // Header: mark + 3 nav pills + a wider action chip.
  {
    id: 'hdr-mark',
    group: 'header',
    indexInGroup: 0,
    x: 0,
    y: 0,
    w: 28,
    h: 28,
    radius: 14,
    tone: 'accent',
    dx: -60,
    dy: -40,
    ddx: 40,
    ddy: -30,
    rot: -8,
    overshoot: 1.08,
  },
  {
    id: 'hdr-pill-1',
    group: 'header',
    indexInGroup: 1,
    x: 44,
    y: 6,
    w: 40,
    h: 16,
    radius: 8,
    tone: 'base',
    dx: -30,
    dy: -50,
    ddx: 50,
    ddy: -20,
    rot: 6,
    overshoot: 1.06,
  },
  {
    id: 'hdr-pill-2',
    group: 'header',
    indexInGroup: 2,
    x: 92,
    y: 6,
    w: 40,
    h: 16,
    radius: 8,
    tone: 'base',
    dx: 20,
    dy: -55,
    ddx: -40,
    ddy: -25,
    rot: -5,
    overshoot: 1.06,
  },
  {
    id: 'hdr-pill-3',
    group: 'header',
    indexInGroup: 3,
    x: 140,
    y: 6,
    w: 40,
    h: 16,
    radius: 8,
    tone: 'base',
    dx: 60,
    dy: -45,
    ddx: -55,
    ddy: -15,
    rot: 7,
    overshoot: 1.06,
  },
  {
    id: 'hdr-cta',
    group: 'header',
    indexInGroup: 4,
    x: 340,
    y: 4,
    w: 80,
    h: 20,
    radius: 10,
    tone: 'accent',
    dx: 90,
    dy: -50,
    ddx: -70,
    ddy: -35,
    rot: -4,
    overshoot: 1.05,
    shimmer: true,
  },

  // Hero: one large surface -- softer overshoot, quint-out timing (see `big` flag below).
  {
    id: 'hero',
    group: 'hero',
    indexInGroup: 0,
    x: 0,
    y: 44,
    w: 420,
    h: 92,
    radius: 16,
    tone: 'base',
    dx: 0,
    dy: -70,
    ddx: 0,
    ddy: 60,
    rot: -2,
    overshoot: 1.015,
    big: true,
    shimmer: true,
  },

  // Three-card row: thumbnail + 2 text lines each.
  {
    id: 'card-1-thumb',
    group: 'cards',
    indexInGroup: 0,
    x: 0,
    y: 150,
    w: 128,
    h: 60,
    radius: 12,
    tone: 'base',
    dx: -80,
    dy: 40,
    ddx: 60,
    ddy: 40,
    rot: -6,
    overshoot: 1.03,
    big: true,
    shimmer: true,
  },
  {
    id: 'card-1-line-1',
    group: 'cards',
    indexInGroup: 1,
    x: 0,
    y: 216,
    w: 96,
    h: 10,
    radius: 5,
    tone: 'base',
    dx: -70,
    dy: 55,
    ddx: 55,
    ddy: 50,
    rot: 4,
    overshoot: 1.06,
  },
  {
    id: 'card-1-line-2',
    group: 'cards',
    indexInGroup: 2,
    x: 0,
    y: 232,
    w: 64,
    h: 10,
    radius: 5,
    tone: 'base',
    dx: -60,
    dy: 65,
    ddx: 45,
    ddy: 60,
    rot: -3,
    overshoot: 1.06,
  },
  {
    id: 'card-2-thumb',
    group: 'cards',
    indexInGroup: 3,
    x: 146,
    y: 150,
    w: 128,
    h: 60,
    radius: 12,
    tone: 'base',
    dx: 0,
    dy: 60,
    ddx: 0,
    ddy: -55,
    rot: 5,
    overshoot: 1.03,
    big: true,
    shimmer: true,
  },
  {
    id: 'card-2-line-1',
    group: 'cards',
    indexInGroup: 4,
    x: 146,
    y: 216,
    w: 96,
    h: 10,
    radius: 5,
    tone: 'base',
    dx: 10,
    dy: 55,
    ddx: -10,
    ddy: 50,
    rot: -4,
    overshoot: 1.06,
  },
  {
    id: 'card-2-line-2',
    group: 'cards',
    indexInGroup: 5,
    x: 146,
    y: 232,
    w: 64,
    h: 10,
    radius: 5,
    tone: 'base',
    dx: 20,
    dy: 65,
    ddx: -25,
    ddy: 55,
    rot: 3,
    overshoot: 1.06,
  },
  {
    id: 'card-3-thumb',
    group: 'cards',
    indexInGroup: 6,
    x: 292,
    y: 150,
    w: 128,
    h: 60,
    radius: 12,
    tone: 'accent',
    dx: 80,
    dy: 40,
    ddx: -60,
    ddy: 40,
    rot: 6,
    overshoot: 1.03,
    big: true,
    shimmer: true,
  },
  {
    id: 'card-3-line-1',
    group: 'cards',
    indexInGroup: 7,
    x: 292,
    y: 216,
    w: 96,
    h: 10,
    radius: 5,
    tone: 'base',
    dx: 70,
    dy: 55,
    ddx: -55,
    ddy: 50,
    rot: -5,
    overshoot: 1.06,
  },
  {
    id: 'card-3-line-2',
    group: 'cards',
    indexInGroup: 8,
    x: 292,
    y: 232,
    w: 64,
    h: 10,
    radius: 5,
    tone: 'base',
    dx: 60,
    dy: 65,
    ddx: -45,
    ddy: 60,
    rot: 4,
    overshoot: 1.06,
  },

  // Ragged paragraph lines: 100% / 92% / 68% / 45% widths of the 420px stage.
  {
    id: 'line-1',
    group: 'lines',
    indexInGroup: 0,
    x: 0,
    y: 258,
    w: 420,
    h: 8,
    radius: 4,
    tone: 'base',
    dx: -40,
    dy: 30,
    ddx: 40,
    ddy: -25,
    rot: 2,
    overshoot: 1.07,
  },
  {
    id: 'line-2',
    group: 'lines',
    indexInGroup: 1,
    x: 0,
    y: 270,
    w: 386,
    h: 8,
    radius: 4,
    tone: 'base',
    dx: 30,
    dy: 35,
    ddx: -35,
    ddy: -30,
    rot: -2,
    overshoot: 1.07,
  },
  {
    id: 'line-3',
    group: 'lines',
    indexInGroup: 2,
    x: 0,
    y: 282,
    w: 286,
    h: 8,
    radius: 4,
    tone: 'base',
    dx: -25,
    dy: 40,
    ddx: 30,
    ddy: -35,
    rot: 3,
    overshoot: 1.07,
  },
  {
    id: 'line-4',
    group: 'lines',
    indexInGroup: 3,
    x: 0,
    y: 294,
    w: 189,
    h: 8,
    radius: 4,
    tone: 'base',
    dx: 20,
    dy: 45,
    ddx: -25,
    ddy: -40,
    rot: -3,
    overshoot: 1.07,
  },
];

/** Precomputed per-block delay: group beat + within-group stagger. Kept as a plain number (ms)
 * rather than an index, so the "<= ~40% of hold" budget documented above GROUP_BEAT_MS is
 * checkable by eye here rather than buried in a CSS calc().
 *
 * The `cards` group has 3 cards x (1 thumbnail + 2 text lines) = 9 members. Giving each of the
 * 9 its own MICRO_STAGGER_MS step (as a flat index) would spread (9 - 1) * MICRO_STAGGER_MS ms
 * *within the group alone*, on top of the group beat -- blowing the hold budget (see FIX 3 in
 * review). Instead, each card gets one per-card stagger step (0/1/2), and its two text lines
 * ride a much smaller INTRA_CARD_STAGGER_MS on top so the whole card reads as one arriving
 * unit. */
function delayForBlock(block: BlockDescriptor): number {
  const groupDelay = GROUP_ORDER[block.group] * GROUP_BEAT_MS;
  if (block.group === 'cards') {
    const cardIndex = Math.floor(block.indexInGroup / 3);
    const withinCardIndex = block.indexInGroup % 3;
    return groupDelay + cardIndex * MICRO_STAGGER_MS + withinCardIndex * INTRA_CARD_STAGGER_MS;
  }
  return groupDelay + block.indexInGroup * MICRO_STAGGER_MS;
}

type EditorAssemblyLoaderProps = {
  /** Optional caption override -- defaults to the exact string existing tests query for. */
  caption?: string;
};

/**
 * Looping "page assembly" skeleton animation shown while the CMS editor iframe boots.
 *
 * Pure CSS keyframes, no JS timers/state/re-renders: the DOM is static, and every block's
 * scatter vector + delay is baked into inline custom properties at first render. This keeps the
 * animation running on the compositor even while the main thread is busy with editor bootstrap
 * work -- the whole point of a loading screen (see PLAN.md's "Out of scope" on framer-motion).
 */
function EditorAssemblyLoaderImpl({ caption = 'Loading editor...' }: EditorAssemblyLoaderProps): JSX.Element {
  return (
    <Container data-testid='cms-assembly-loader' role='status' aria-live='polite' aria-busy='true'>
      <Stage aria-hidden='true'>
        {BLOCKS.map((block) => (
          <Block
            key={block.id}
            $tone={block.tone}
            $big={Boolean(block.big)}
            $shimmer={Boolean(block.shimmer)}
            style={
              {
                left: `${block.x}px`,
                top: `${block.y}px`,
                width: `${block.w}px`,
                height: `${block.h}px`,
                borderRadius: `${block.radius}px`,
                '--delay': `${delayForBlock(block)}ms`,
                '--dx': `${block.dx}px`,
                '--dy': `${block.dy}px`,
                '--ddx': `${block.ddx}px`,
                '--ddy': `${block.ddy}px`,
                '--rot': `${block.rot}deg`,
                '--overshoot': block.overshoot,
              } as React.CSSProperties
            }
          />
        ))}
      </Stage>
      <Caption>{caption}</Caption>
    </Container>
  );
}

export const EditorAssemblyLoader = React.memo(EditorAssemblyLoaderImpl);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

// Fixed-size stage so every block's x/y/dx/dy is a deterministic px value. Scaled down (a
// transform, not a resize) below the stage's own footprint so this still fits the <638px
// LoadingOverlay path without recomputing the layout.
const Stage = styled.div`
  position: relative;
  width: 420px;
  height: 300px;

  /* Deliberately NOT "transform: scale(min(1, calc((100vw - 32px) / 420px)))": dividing a
     <length> by a <length> to get a unitless number needs CSS typed arithmetic, supported only
     in Chrome 140+ / Safari 26+. Firefox has no support (bugzilla.mozilla.org/show_bug.cgi?id=
     1827404), so there the whole declaration is invalid and dropped, silently reintroducing the
     never-scales-down bug this fixes. See caniuse.com/mdn-css_types_calc_typed_division_produces_unitless_number.
     Use discrete, universally-valid breakpoints instead. Invariant checked below: 420 * scale + 32
     <= narrowest width the tier actually applies at (its effective floor is the next breakpoint's
     max-width + 1, since a later, more specific media query overrides an earlier one). Fit check:
     scale(0.9) covers 561-638 => 420*0.9+32=410<=561; scale(0.82) covers 481-560 =>
     420*0.82+32=376.4<=481; scale(0.72) covers 401-480 => 420*0.72+32=334.4<=401; scale(0.66)
     covers <=400 (floor 320, e.g. iPhone SE) => 420*0.66+32=309.2<=320. */
  @media (max-width: 638px) {
    transform: scale(0.9);
  }

  @media (max-width: 560px) {
    transform: scale(0.82);
  }

  @media (max-width: 480px) {
    transform: scale(0.72);
  }

  @media (max-width: 400px) {
    transform: scale(0.66);
  }
`;

const Caption = styled.div`
  font-size: 14px;
  color: #646d72;
`;

/**
 * One shared timeline for every block. `0%` and `100%` are byte-identical (both fully
 * transparent, scattered) so the loop seam is invisible -- there's nothing on screen at the
 * splice for a viewer to notice repeating.
 *
 * Per-step `animation-timing-function` applies to the segment *starting* at that step, which is
 * what lets a single `animation` mix easing curves across phases (fly-in overshoot vs.
 * dissolve's symmetric ease) without juggling multiple animations per block.
 */
const Block = styled.div<{ $tone: BlockTone; $big: boolean; $shimmer: boolean }>`
  position: absolute;
  background: ${({ $tone }) => ($tone === 'accent' ? TONE_ACCENT : TONE_BASE)};
  border: ${TONE_HAIRLINE};
  overflow: hidden;
  will-change: transform, opacity;

  animation-name: assemble;
  animation-duration: ${LOOP_MS}ms;
  animation-iteration-count: infinite;
  animation-delay: var(--delay);
  /* Without this, a block whose delay hasn't elapsed yet renders with the element's default
     (un-animated) styles -- fully opaque, untransformed -- until its delay expires, so first
     paint flashes "fully assembled" before the scatter kicks in. 'backwards' makes the 0%
     keyframe apply during the delay instead. */
  animation-fill-mode: backwards;
  /* Big surfaces (hero, card thumbnails) use quint-out with ~no overshoot; everything else
     gets the back-out overshoot. Both live inside the single assemble keyframes via
     per-step timing functions -- this just tips which "flavour" of overshoot a block settles
     into via its own --overshoot custom property, consumed in the keyframes below. */
  animation-timing-function: ${({ $big }) =>
    $big ? 'cubic-bezier(.22, 1, .36, 1)' : 'cubic-bezier(.34, 1.56, .64, 1)'};

  ${({ $shimmer }) =>
    $shimmer &&
    `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(100deg, transparent 30%, ${TONE_HIGHLIGHT} 50%, transparent 70%);
      background-size: 200% 100%;
      /* Gated to the hold phase (46-74%) by the shimmer keyframes' own timeline below --
         off-phase this is just a transparent no-op layer, cheap to keep mounted. */
      animation: shimmer ${LOOP_MS}ms infinite;
      animation-delay: var(--delay);
    }
  `}

  @keyframes assemble {
    /* Offstage (0-8%): scattered via --dx/--dy/--rot, fully transparent. */
    0% {
      opacity: 0;
      transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(0.9);
    }
    /* No timing-function override here: this used to be 'animation-timing-function: inherit',
       which inside a keyframe resolves against the *parent DOM element's* computed value (the
       Stage/Container declare none, so it silently fell back to the initial 'ease'), discarding
       the per-block easeOutBack/easeOutQuint curve set on Block above. Falling through to that
       per-element curve is what actually gives big vs. small blocks distinct fly-in easing. */
    8% {
      opacity: 0;
      transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(0.9);
    }
    /* Fly-in (8-40%): travel to final position with a slight overshoot before settling --
       the overshoot amount itself comes from --overshoot (bigger blocks get a value near 1,
       set in BLOCKS, so they don't visibly overshoot even though they share this timeline).
       Shortened vs. the previous 8-46% split (fly-in now finishes sooner, in ms, even though
       the loop itself is longer) so the fly-in stays energetic while the freed-up percentage
       buys extra hold time below. */
    34% {
      opacity: 1;
      transform: translate(0, 0) rotate(0deg) scale(var(--overshoot));
      /* A step's animation-timing-function shapes the segment that *starts* here, not the one
         ending here. This curve governs 34%->40% (the overshoot settling back to scale(1)) --
         easeInOutQuint gives that settle a distinct, symmetric ease-in/ease-out feel, different
         from the fly-in's back/quint-out curve declared on Block. */
      animation-timing-function: cubic-bezier(0.83, 0, 0.17, 1);
    }
    40% {
      opacity: 1;
      transform: translate(0, 0) rotate(0deg) scale(1);
    }
    /* Hold (40-72%): assembled and still; shimmer (separate keyframes) sweeps during this
       window. Widened from the previous 46-74% (28% of a 4200ms loop = 1176ms) to 32% of a
       4500ms loop = 1440ms -- see the budget-rule comment above GROUP_BEAT_MS for the full
       assembled-window arithmetic this buys. */
    72% {
      opacity: 1;
      transform: translate(0, 0) rotate(0deg) scale(1);
      /* Governs 72%->90%, the dissolve drift below -- same easeInOutQuint curve as the settle
         segment, reused here for the symmetric ease-out-like fade so dissolve doesn't read as
         a linear or default-eased fade. */
      animation-timing-function: cubic-bezier(0.83, 0, 0.17, 1);
    }
    /* Dissolve (72-90%): drifts out along --ddx/--ddy, a different vector than it arrived on,
       so arrival and departure never read as the same motion in reverse. */
    90% {
      opacity: 0;
      transform: translate(var(--ddx), var(--ddy)) rotate(calc(var(--rot) * -1)) scale(0.92);
    }
    /* Reset (90-100%): returns to a byte-identical copy of 0% -- see the function doc comment
       for why this exact frame must match 0% exactly. */
    100% {
      opacity: 0;
      transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(0.9);
    }
  }

  @keyframes shimmer {
    /* Transparent (no-op) everywhere except the hold window, so the sweep only reads while
       the block is actually assembled and sitting still. Retuned to the widened 40-72% hold
       window: flat until just before hold starts, sweep crosses at the window's midpoint
       (56%), flat again from hold-end to the loop seam. */
    0%,
    38% {
      background-position: 200% 0;
    }
    40% {
      background-position: 200% 0;
    }
    56% {
      background-position: -200% 0;
    }
    72%,
    100% {
      background-position: -200% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: none;
    opacity: 1;
    will-change: auto;

    &::after {
      animation: none;
      background: none;
    }
  }
`;
